import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/api/client';
import { formatCompact } from './TrendChart';

/** Poll interval. GA4's realtime quota is separate from the historical one, so this cannot
 *  starve the nightly ingestion — but 60s is plenty for a "is the site alive" glance. */
const REFRESH_MS = 60_000;

const BAR_W = 10;
const GAP = 2; // the surface gap — white does the separating, never a stroke
const CHART_H = 56;

/**
 * "Right now" card, backed by GA4's Realtime API (last ~30 minutes).
 *
 * Renders nothing when the site has no GA4 source (204) — a Clarity-only site is a normal
 * configuration. On a provider error it degrades to a quiet line rather than an alarm: the
 * historical dashboard below is the primary content and must stay usable.
 */
export function LiveCard({ siteKey }: { siteKey: string }) {
  const { data, isError, error, isLoading } = useQuery({
    queryKey: ['realtime', siteKey],
    queryFn: () => api.realtime(siteKey),
    refetchInterval: REFRESH_MS,
    // This one SHOULD self-heal on focus — it is a live view, unlike the historical queries.
    refetchOnWindowFocus: true,
    staleTime: 0,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="card card-body">
        <span className="spinner" aria-label="Loading live data" />
      </div>
    );
  }

  // 204 → no GA4 source for this site. Render nothing at all.
  if (!isError && !data) return null;

  if (isError) {
    return (
      <div className="card card-body">
        <div className="t-label muted">Right now</div>
        <div className="t-body subtle" style={{ marginTop: 4 }}>
          Live data unavailable
          {error instanceof ApiError && error.status === 503 ? ' — GA4 did not respond.' : '.'}{' '}
          Historical data below is unaffected.
        </div>
      </div>
    );
  }

  const snapshot = data!;
  const max = Math.max(...snapshot.by_minute.map((m) => m.active_users), 1);

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="live-dot" aria-hidden="true" />
          Right now
        </h2>
        <span className="t-label subtle">Google Analytics · last 30 min</span>
      </div>

      <div className="card-body" style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 140 }}>
          {/* Hero figure — proportional numerals; tabular-nums looks loose at this size. */}
          <div className="stat-label t-label">Active users</div>
          <div style={{ fontSize: 44, lineHeight: '52px', fontWeight: 600, letterSpacing: '-0.02em' }}>
            {snapshot.active_users}
          </div>
          <div className="t-caption subtle">{formatCompact(snapshot.page_views)} page views</div>
        </div>

        <div style={{ flex: 1, minWidth: 260 }}>
          <div className="t-label muted" style={{ marginBottom: 8 }}>
            Active users per minute
          </div>
          {/* Columns, not a line: each minute is a discrete bucket, and a line would imply
              continuity between samples. One hue — this is a single measure over time. */}
          <svg
            className="chart-svg"
            viewBox={`0 0 ${snapshot.by_minute.length * (BAR_W + GAP)} ${CHART_H}`}
            height={CHART_H}
            role="img"
            aria-label={`Active users per minute over the last ${snapshot.by_minute.length} minutes`}
          >
            {snapshot.by_minute.map((minute, i) => {
              const h = Math.max(2, (minute.active_users / max) * (CHART_H - 4));
              return (
                <rect
                  key={minute.minutes_ago}
                  x={i * (BAR_W + GAP)}
                  y={CHART_H - h}
                  width={BAR_W}
                  height={h}
                  rx="4"
                  fill="var(--series-1)"
                  opacity={minute.active_users === 0 ? 0.18 : 1}
                >
                  <title>
                    {minute.minutes_ago === 0 ? 'now' : `${minute.minutes_ago} min ago`}:{' '}
                    {minute.active_users} active
                  </title>
                </rect>
              );
            })}
          </svg>
          <div className="t-caption subtle" style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>30 min ago</span>
            <span>now</span>
          </div>
        </div>

        {snapshot.top_pages.length > 0 && (
          <div style={{ minWidth: 200 }}>
            <div className="t-label muted" style={{ marginBottom: 8 }}>
              Top pages now
            </div>
            <table className="table" style={{ fontSize: 'var(--text-label)' }}>
              <tbody>
                {snapshot.top_pages.map((page) => (
                  <tr key={page.value}>
                    <td style={{ padding: '4px 8px 4px 0', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {page.value || '(not set)'}
                    </td>
                    <td className="num" style={{ padding: '4px 0' }}>{page.active_users}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
